'use client';

import {ErrorSection} from '@/app/_components/shared/error-section';
import {useLocale} from '@/app/_contexts/locale-context';
import {errorLabels, type ErrorLabelLocale} from '@/app/_lib/error-labels.generated';

interface ErrorProps {
    error: Error & { digest?: string };
    reset: () => void;
}

export default function ErrorPage({error, reset}: ErrorProps) {
    const locale = useLocale();

    // Labels come from a frozen TYPO3 snapshot (error-labels.generated.ts), not
    // the live API or a client cache — so this page renders localized text even
    // when the backend is down. Locale is derived from the URL, never the API.
    // locale comes from the URL and can be anything — check membership for real
    // instead of a lying cast that types the lookup as always-defined.
    const globalLabels = Object.hasOwn(errorLabels, locale)
      ? errorLabels[locale as ErrorLabelLocale]
      : errorLabels.de;

    const tagline = globalLabels.appErrorTagline || 'Application Error';
    const title = globalLabels.appErrorTitle || 'Something went wrong';
    const description = globalLabels.appErrorDescription || 'An unexpected error occurred while loading this page. Please try again or navigate back to the homepage.';
    const resetButtonLabel = globalLabels.resetButtonLabel || 'Try again';
    const defaultButtonLabel = globalLabels.homeButtonLabel || 'Go to homepage';
    const homeHref = locale !== 'de' ? `/${locale}` : '/';

    return (
        <ErrorSection
            tagline={tagline}
            title={title}
            description={description}
            resetButtonLabel={resetButtonLabel}
            defaultButtonLabel={defaultButtonLabel}
            homeHref={homeHref}
            showResetButton={true}
            onReset={reset}
            error={error}
        />
    );
}
